import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, Search, RefreshCw } from 'lucide-react';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import Pagination from '../components/Pagination';
import StatusPill from '../components/StatusPill';
import FilterBar, { controlClass, secondaryButtonClass } from '../components/FilterBar';
import OpenInAppButton from '../components/OpenInAppButton';
import { PieChartCard, CHART_COLORS } from '../components/charts';
import { formatCurrency, formatDate } from '../lib/format';
import { fetchTransactionsChart, searchTransactions, type TransactionRow } from '../lib/api';

const PAGE_SIZE = 25;

const SOURCE_TONE: Record<string, 'info' | 'warning' | 'success' | 'neutral'> = {
  mercury: 'info',
  paypal: 'warning',
  chain2pay: 'success',
};

const columns: Column<TransactionRow>[] = [
  { key: 'date', header: 'Date', render: (r) => formatDate(r.date) },
  {
    key: 'source',
    header: 'Source',
    render: (r) => <StatusPill status={r.source} tone={SOURCE_TONE[r.source] ?? 'neutral'} label={r.source} />,
  },
  { key: 'description', header: 'Description', render: (r) => r.description || '—' },
  { key: 'counterparty', header: 'Counterparty', render: (r) => r.counterparty || '—' },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (r) => (
      <span className={Number(r.amount) < 0 ? 'text-zt-danger' : 'text-slate-200'}>
        {formatCurrency(r.amount, r.currency)}
      </span>
    ),
  },
  { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
];

function Transactions() {
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categorySlices, setCategorySlices] = useState<Array<{ name: string; value: number; color?: string }>>([]);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ query: '', status: 'all', startDate: '', endDate: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const result = await searchTransactions({
      query: appliedFilters.query || undefined,
      status: appliedFilters.status,
      start_date: appliedFilters.startDate || undefined,
      end_date: appliedFilters.endDate || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    });
    if (result.ok && result.data) {
      setRows(result.data.data ?? []);
      setTotal(result.data.total ?? 0);
    } else {
      setRows([]);
      setTotal(0);
    }
    setLoading(false);
  }, [appliedFilters, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    async function loadCategories() {
      setCategoryLoading(true);
      const result = await fetchTransactionsChart();
      if (!cancelled) {
        const slices =
          result.ok && result.data?.data
            ? result.data.data.map((row, i) => ({
                name: row.category,
                value: row.amount,
                color: CHART_COLORS[i % CHART_COLORS.length],
              }))
            : [];
        setCategorySlices(slices);
        setCategoryLoading(false);
      }
    }
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const categorySubtitle = useMemo(() => {
    const totalSpend = categorySlices.reduce((sum, s) => sum + s.value, 0);
    return totalSpend > 0 ? `Outgoing spend · ${formatCurrency(totalSpend)} total` : 'Outgoing spend by category';
  }, [categorySlices]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ query, status, startDate, endDate });
  };

  const postedCount = rows.filter((r) => r.status === 'posted' || r.status === 'completed').length;

  return (
    <PageShell
      title="Transactions"
      subtitle="Unified bank, card, and crypto transactions from Mercury, PayPal, and Chain2Pay."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Matching total" value={loading ? '…' : String(total)} tone="info" icon={<ArrowLeftRight size={18} />} />
        <StatCard label="On this page" value={loading ? '…' : String(rows.length)} tone="neutral" />
        <StatCard label="Posted (page)" value={loading ? '…' : String(postedCount)} tone="success" />
        <StatCard label="Page" value={`${page}`} tone="neutral" />
      </div>

      <PieChartCard
        title="Spending by category"
        subtitle={categorySubtitle}
        data={categorySlices}
        loading={categoryLoading}
        emptyTitle="No spending data"
        emptyMessage="Sync Mercury, PayPal, or Chain2Pay to populate category breakdown."
      />

      <FilterBar
        actions={
          <>
            <OpenInAppButton integrationKey="erpnext" />
            <button type="button" onClick={applyFilters} className={secondaryButtonClass}>
              <Search size={16} /> Apply
            </button>
          </>
        }
      >
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          placeholder="Search payee, memo, reference…"
          className={`w-56 ${controlClass}`}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={controlClass}>
          <option value="all">All statuses</option>
          <option value="posted">Posted</option>
          <option value="pending">Pending</option>
          <option value="reconciled">Reconciled</option>
          <option value="flagged">Flagged</option>
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className={controlClass}
          aria-label="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className={controlClass}
          aria-label="End date"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r, i) => `${r.source}-${r.id}-${i}`}
        loading={loading}
        emptyTitle="No transactions found"
        emptyMessage="Try adjusting your filters, or sync a payment integration to populate transactions."
        emptyIcon={<ArrowLeftRight size={32} />}
        emptyAction={
          <button type="button" onClick={load} className={secondaryButtonClass}>
            <RefreshCw size={16} /> Reload
          </button>
        }
      />

      {total > PAGE_SIZE && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}
    </PageShell>
  );
}

export default Transactions;
